import axios from 'axios';
import API_URL from '../api';

/* ==================================================================
   Razorpay Checkout orchestration
   ------------------------------------------------------------------
   One place that owns the whole payment conversation, so no screen
   has to re-derive it. Every caller gets the same state machine and
   the same guarantees.

   THE RULE THIS FILE EXISTS TO ENFORCE:
   Razorpay Checkout closing is NOT success. The handler below runs in
   the browser and proves nothing - only the backend, having recomputed
   the signature against its own secret, can say a payment happened.
   So `verifying` is a real state the user sees, and `success` is
   reached only from a 200 on /api/billing/verify.

   States, in the order they can occur:
     idle -> creating -> opening -> verifying -> success
                             |          |
                             |          +-> failed  (verification rejected)
                             +-> cancelled           (user dismissed)
                             +-> failed              (Razorpay reported failure)
   ================================================================== */

const SCRIPT_SRC = 'https://checkout.razorpay.com/v1/checkout.js';

let scriptPromise = null;

/**
 * Load Razorpay's checkout script once per page.
 *
 * Memoised on the promise rather than on a boolean: two cards clicked in quick
 * succession would otherwise both see "not loaded yet" and inject two tags.
 */
export function loadRazorpayScript() {
  if (window.Razorpay) return Promise.resolve(true);
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(true));
      existing.addEventListener('error', () => reject(new Error('checkout_script_failed')));
      return;
    }
    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => {
      // Let a later attempt retry rather than caching the failure forever —
      // this is usually a transient network problem or an ad blocker the user
      // can turn off without reloading.
      scriptPromise = null;
      reject(new Error('checkout_script_failed'));
    };
    document.body.appendChild(script);
  });

  return scriptPromise;
}

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('token')}` };
}

/** Turn any failure into something a recruiter can act on. */
export function friendlyPaymentError(err) {
  const status = err?.response?.status;
  const code = err?.response?.data?.code || err?.code;
  const message = err?.response?.data?.error;

  if (code === 'RAZORPAY_NOT_CONFIGURED') {
    return 'Payments are temporarily unavailable. Please try again shortly or contact support.';
  }
  if (code === 'checkout_script_failed' || err?.message === 'checkout_script_failed') {
    return 'The payment window could not load. Check your connection (or an ad blocker) and try again.';
  }
  if (code === 'SIGNATURE_INVALID') {
    return message || 'We could not verify this payment. If money was debited it will be refunded automatically.';
  }
  if (code === 'AMOUNT_MISMATCH') {
    return message || 'The payment amount did not match the selected package. Nothing has been charged to your account.';
  }
  if (status === 403) return message || 'This order belongs to another account.';
  if (status === 401) return 'Your session expired. Please sign in again.';
  if (!err?.response) {
    // Verification specifically: the money may well have gone through, so the
    // wording must not tell the user the payment failed.
    return 'We lost connection while confirming your payment. It may still have gone through — reload this page in a moment before trying again.';
  }
  return message || 'Something went wrong with the payment. Please try again.';
}

/**
 * Run one purchase end to end.
 *
 * @param {string}   packageKey  catalog key — the ONLY thing sent to the server.
 *                               Price, credits and validity are the server's.
 * @param {function} onState     (state, detail) for every transition.
 * @returns {Promise<{ status, payment?, entitlements?, error? }>}
 *          Always resolves. Cancellation is an outcome, not an exception.
 */
export async function purchasePackage(packageKey, onState = () => {}) {
  let paymentId = null;

  try {
    onState('creating');

    // The script loads alongside order creation rather than after it — they are
    // independent, and doing them in sequence adds a visible pause before the
    // checkout window appears.
    const [orderRes] = await Promise.all([
      axios.post(`${API_URL}/api/billing/order`, { package_key: packageKey }, { headers: authHeaders() }),
      loadRazorpayScript(),
    ]);

    const { checkout, payment_id: id } = orderRes.data;
    paymentId = id;

    onState('opening', { checkout });

    // The Razorpay modal is callback-based; wrap it so the caller can await the
    // final outcome instead of threading callbacks through component state.
    const outcome = await new Promise((resolve) => {
      let settled = false;
      const settle = (value) => {
        if (settled) return;
        settled = true;
        resolve(value);
      };

      const rzp = new window.Razorpay({
        key: checkout.key,
        order_id: checkout.order_id,
        amount: checkout.amount,
        currency: checkout.currency,
        name: checkout.name,
        description: checkout.description,
        prefill: checkout.prefill,
        notes: checkout.notes,
        theme: checkout.theme,
        // Razorpay's own retry UI would re-enter checkout behind our state
        // machine's back, leaving the page showing "opening" forever.
        retry: { enabled: false },
        modal: {
          ondismiss: () => settle({ kind: 'cancelled' }),
          escape: true,
        },
        handler: (response) => settle({ kind: 'completed', response }),
      });

      rzp.on('payment.failed', (event) => {
        settle({
          kind: 'failed',
          reason: event?.error?.description || 'Payment failed',
          code: event?.error?.code || null,
        });
      });

      rzp.open();
    });

    if (outcome.kind === 'cancelled') {
      // Advisory only — the server refuses to mark anything paid as failed, so
      // a payment that actually completed is still settled by the webhook.
      reportFailure(paymentId, 'Cancelled at checkout', 'USER_CANCELLED');
      onState('cancelled');
      return { status: 'cancelled', paymentId };
    }

    if (outcome.kind === 'failed') {
      reportFailure(paymentId, outcome.reason, outcome.code);
      onState('failed', { error: outcome.reason });
      return { status: 'failed', error: outcome.reason, paymentId };
    }

    // Checkout says it completed. That is a hint, not a fact — verify it.
    onState('verifying');

    const verifyRes = await axios.post(
      `${API_URL}/api/billing/verify`,
      {
        razorpay_order_id: outcome.response.razorpay_order_id,
        razorpay_payment_id: outcome.response.razorpay_payment_id,
        razorpay_signature: outcome.response.razorpay_signature,
      },
      { headers: authHeaders() }
    );

    onState('success', verifyRes.data);
    return {
      status: 'success',
      payment: verifyRes.data.payment,
      entitlements: verifyRes.data.entitlements,
      alreadyProcessed: verifyRes.data.already_processed,
      paymentId,
    };
  } catch (err) {
    const message = friendlyPaymentError(err);

    // A network failure DURING verification is the one case where we genuinely
    // do not know the outcome. Saying "payment failed" there would be a lie, so
    // it gets its own state and the UI offers to re-check rather than re-pay.
    const unknown = !err?.response && paymentId;
    onState(unknown ? 'unknown' : 'failed', { error: message });
    return {
      status: unknown ? 'unknown' : 'failed',
      error: message,
      paymentId,
    };
  }
}

/** Tell the server an attempt was abandoned. Never blocks or throws. */
function reportFailure(paymentId, reason, code) {
  if (!paymentId) return;
  axios.post(
    `${API_URL}/api/billing/payments/${paymentId}/failed`,
    { reason, code },
    { headers: authHeaders() }
  ).catch(() => { /* advisory: the webhook is the authority */ });
}

/**
 * Ask the server to settle an order whose outcome we never saw.
 *
 * `reconcile=true` makes the backend query Razorpay directly, which is what
 * recovers a payment where the browser closed before the callback fired.
 */
export async function checkPaymentStatus(paymentId) {
  const res = await axios.get(
    `${API_URL}/api/billing/payments/${paymentId}?reconcile=true`,
    { headers: authHeaders() }
  );
  return res.data;
}

/** Live entitlements for both products. */
export async function fetchEntitlements() {
  const res = await axios.get(`${API_URL}/api/billing/entitlements`, { headers: authHeaders() });
  return res.data;
}

/** The catalog. Server-priced; the UI only renders it. */
export async function fetchPackages() {
  const res = await axios.get(`${API_URL}/api/billing/packages`);
  return res.data;
}

/** ₹1,499 from 149900 paise. The single formatter for the frontend. */
export function formatInr(paise) {
  if (paise == null) return '—';
  return `₹${(paise / 100).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
}

/** "24 Oct 2026" — the one date format used across billing surfaces. */
export function formatDate(value) {
  if (!value) return null;
  return new Date(value).toLocaleDateString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

/** Whole days until `value`, or null. Negative clamps to 0. */
export function daysUntil(value) {
  if (!value) return null;
  const ms = new Date(value).getTime() - Date.now();
  return ms <= 0 ? 0 : Math.ceil(ms / 86400000);
}
